import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import type { TaskTeam } from "@/features/tasks/types/task";
import {
  createTeam,
  updateTeam,
  addMember,
  removeMember,
} from "@/features/tasks/services/taskTeamService";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { formatDisplayName } from "@/shared/utils/formatters";
import { Spinner } from "@/shared/components/ui/Spinner";
import { TaskUserSelect, type TaskUserOption } from "@/features/tasks/components/TaskUserSelect";

interface TeamFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError?: (err: any) => void;
  team?: TaskTeam | null;
}

export const TeamFormModal: React.FC<TeamFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  onError,
  team,
}) => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const isEdit = !!team;

  const [name, setName]             = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError]   = useState("");
  const [saving, setSaving]         = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [addingId, setAddingId]     = useState<string | null>(null);

  const [members, setMembers] = useState<{ user_id: string; display_name: string; profile_pic_id?: string }[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setName(team?.name ?? "");
    setDescription(team?.description ?? "");
    setNameError("");

    if (isEdit && team) {
      setMembers(
        team.members.map((m) => ({
          user_id: m.user_id,
          display_name: formatDisplayName(m.last_name, m.first_name, m.middle_name),
          profile_pic_id: m.profile_pic_id,
        })),
      );
    } else {
      // Auto-add the creator when creating a new team
      const creatorId = String(userData?.id ?? "");
      const creatorName = formatDisplayName(
        userData?.lastName,
        userData?.firstName,
        userData?.middleName,
      );
      setMembers(
        creatorId
          ? [{ user_id: creatorId, display_name: creatorName, profile_pic_id: userData?.profilePicId }]
          : [],
      );
    }
  }, [isOpen, team]);

  // Add a single officer immediately on selection
  const handleAddMember = async (selected: TaskUserOption[]) => {
    const added = selected.find((u) => !members.some((m) => m.user_id === u.id));
    if (!added) return;

    if (!isEdit || !team) {
      setMembers((prev) => [...prev, { user_id: added.id, display_name: added.name }]);
      return;
    }
    setAddingId(added.id);
    try {
      await addMember(team.id, added.id);
      setMembers((prev) => [...prev, { user_id: added.id, display_name: added.name }]);
    } finally {
      setAddingId(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!isEdit || !team) {
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
      return;
    }
    setRemovingId(userId);
    try {
      await removeMember(team.id, userId);
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
    } finally {
      setRemovingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(t("common.required", { defaultValue: "This field is required" }));
      return;
    }
    setNameError("");
    setSaving(true);
    try {
      if (isEdit && team) {
        await updateTeam(team.id, {
          name: name.trim(),
          description: description.trim() || undefined,
        });
      } else {
        const newTeam = await createTeam({
          name: name.trim(),
          description: description.trim() || undefined,
        });
        await Promise.all(members.map((m) => addMember(newTeam.id, m.user_id)));
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Failed to save team", err);
      onError?.(err);
    } finally {
      setSaving(false);
    }
  };

  const creatorId = String(userData?.id ?? "");
  const excludeIds = members.map((m) => m.user_id);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t("tasks.team.edit_title") : t("tasks.team.new_title")}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        <Input
          label={t("tasks.team.field_name")}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError("");
          }}
          placeholder={t("tasks.team.field_name")}
          error={nameError}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("tasks.team.field_description")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("tasks.team.field_members")}
          </label>

          {members.length > 0 && (
            <div className="flex flex-col gap-1.5 mb-3">
              {members.map((m) => (
                <div key={m.user_id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                  <ProfileAvatar
                    picId={m.profile_pic_id}
                    firstName={m.display_name.split(" ")[0]}
                    lastName={m.display_name.split(" ")[1]}
                    size="sm"
                  />
                  <span className="flex-1 text-sm text-gray-700">{m.display_name}</span>
                  {m.user_id === creatorId ? (
                    <span className="text-[10px] text-gray-400 px-1">{t("tasks.team.creator", { defaultValue: "You" })}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(m.user_id)}
                      disabled={removingId === m.user_id || addingId !== null}
                      className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-50"
                      aria-label={t("tasks.team.remove_member")}
                    >
                      {removingId === m.user_id ? <Spinner size="sm" /> : <X className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Officer search — adds immediately on click */}
          <div className="relative">
            <TaskUserSelect
              selected={[]}
              onChange={handleAddMember}
              excludeIds={excludeIds}
              placeholder={t("tasks.team.add_member")}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            {t("tasks.form.cancel")}
          </Button>
          <Button type="submit" loading={saving}>
            {saving ? t("tasks.form.saving") : isEdit ? t("tasks.team.save") : t("tasks.team.create")}
          </Button>
        </div>

      </form>
    </Modal>
  );
};
